/**
 * Theme system — 4 named palettes that swap CSS custom properties at runtime.
 *
 * Each theme overrides --color-primary, --color-accent, --color-hero, and the
 * background gradient stops. Avatars / level pills keep their per-user colors;
 * the theme only changes the global accent stack.
 */

export type ThemeId =
  | 'purple-dawn'
  | 'cyber-mint'
  | 'sunset-pink'
  | 'midnight-blue'
  | 'kids-rainbow'
  | 'ocean-sunshine'
  | 'berry-blast'
  | 'forest-friends';

export interface Theme {
  id: ThemeId;
  nameHe: string;
  description: string;
  /** Preview swatch for the theme picker — gradient string */
  preview: string;
  cssVars: Record<string, string>;
}

export const THEMES: Theme[] = [
  {
    id: 'purple-dawn',
    nameHe: 'שחר סגול',
    description: 'הנושא הברירת מחדל',
    preview: 'linear-gradient(135deg,#8b5cf6 0%,#22d3ee 50%,#f472b6 100%)',
    cssVars: {
      '--color-primary':       '#8b5cf6',
      '--color-primary-light': '#a78bfa',
      '--color-primary-dark':  '#7c3aed',
      '--color-accent':        '#22d3ee',
      '--color-accent-light':  '#67e8f9',
      '--color-accent-dark':   '#0891b2',
      '--color-hero':          '#f472b6',
      '--color-hero-light':    '#f9a8d4',
      '--color-hero-dark':     '#db2777',
      // Background gradient stops (referenced via --bg-glow-*)
      '--bg-glow-1':           'rgba(139,92,246,.10)',
      '--bg-glow-2':           'rgba(34,211,238,.08)',
      '--bg-glow-3':           'rgba(244,114,182,.03)',
    },
  },
  {
    id: 'cyber-mint',
    nameHe: 'מנטה סייבר',
    description: 'ירוק-טורקיז למיקוד',
    preview: 'linear-gradient(135deg,#10b981 0%,#22d3ee 50%,#3b82f6 100%)',
    cssVars: {
      '--color-primary':       '#10b981',
      '--color-primary-light': '#34d399',
      '--color-primary-dark':  '#059669',
      '--color-accent':        '#22d3ee',
      '--color-accent-light':  '#67e8f9',
      '--color-accent-dark':   '#0891b2',
      '--color-hero':          '#3b82f6',
      '--color-hero-light':    '#60a5fa',
      '--color-hero-dark':     '#2563eb',
      '--bg-glow-1':           'rgba(16,185,129,.10)',
      '--bg-glow-2':           'rgba(34,211,238,.08)',
      '--bg-glow-3':           'rgba(59,130,246,.03)',
    },
  },
  {
    id: 'sunset-pink',
    nameHe: 'שקיעה ורודה',
    description: 'אנרגטי וחם',
    preview: 'linear-gradient(135deg,#f97316 0%,#ec4899 50%,#a855f7 100%)',
    cssVars: {
      '--color-primary':       '#ec4899',
      '--color-primary-light': '#f472b6',
      '--color-primary-dark':  '#db2777',
      '--color-accent':        '#f97316',
      '--color-accent-light':  '#fb923c',
      '--color-accent-dark':   '#ea580c',
      '--color-hero':          '#a855f7',
      '--color-hero-light':    '#c084fc',
      '--color-hero-dark':     '#9333ea',
      '--bg-glow-1':           'rgba(236,72,153,.10)',
      '--bg-glow-2':           'rgba(249,115,22,.08)',
      '--bg-glow-3':           'rgba(168,85,247,.03)',
    },
  },
  {
    id: 'midnight-blue',
    nameHe: 'כחול חצות',
    description: 'רגוע וממוקד',
    preview: 'linear-gradient(135deg,#1e40af 0%,#3b82f6 50%,#06b6d4 100%)',
    cssVars: {
      '--color-primary':       '#3b82f6',
      '--color-primary-light': '#60a5fa',
      '--color-primary-dark':  '#2563eb',
      '--color-accent':        '#06b6d4',
      '--color-accent-light':  '#22d3ee',
      '--color-accent-dark':   '#0e7490',
      '--color-hero':          '#1e40af',
      '--color-hero-light':    '#3b82f6',
      '--color-hero-dark':     '#1e3a8a',
      '--bg-glow-1':           'rgba(59,130,246,.10)',
      '--bg-glow-2':           'rgba(6,182,212,.08)',
      '--bg-glow-3':           'rgba(30,64,175,.03)',
    },
  },
  {
    id: 'kids-rainbow',
    nameHe: '🌈 קשת ילדים',
    description: 'חם וצבעוני — אנרגיה',
    preview: 'linear-gradient(135deg,#fbbf24 0%,#f97316 33%,#ec4899 66%,#8b5cf6 100%)',
    cssVars: {
      '--color-primary':       '#f97316',
      '--color-primary-light': '#fb923c',
      '--color-primary-dark':  '#ea580c',
      '--color-accent':        '#fbbf24',
      '--color-accent-light':  '#fcd34d',
      '--color-accent-dark':   '#d97706',
      '--color-hero':          '#ec4899',
      '--color-hero-light':    '#f472b6',
      '--color-hero-dark':     '#db2777',
      '--bg-glow-1':           'rgba(249,115,22,.18)',
      '--bg-glow-2':           'rgba(251,191,36,.14)',
      '--bg-glow-3':           'rgba(236,72,153,.08)',
    },
  },
  {
    id: 'ocean-sunshine',
    nameHe: '☀️ אוקיינוס ושמש',
    description: 'תכלת ים + צהוב שמש',
    preview: 'linear-gradient(135deg,#22d3ee 0%,#67e8f9 50%,#fbbf24 100%)',
    cssVars: {
      // Bright cyan primary — playful + readable
      '--color-primary':       '#22d3ee',
      '--color-primary-light': '#67e8f9',
      '--color-primary-dark':  '#0891b2',
      // Sunshine yellow accent
      '--color-accent':        '#fbbf24',
      '--color-accent-light':  '#fcd34d',
      '--color-accent-dark':   '#d97706',
      // Coral hero
      '--color-hero':          '#fb7185',
      '--color-hero-light':    '#fda4af',
      '--color-hero-dark':     '#e11d48',
      // Brighter glow stops — feels lighter overall
      '--bg-glow-1':           'rgba(34,211,238,.20)',
      '--bg-glow-2':           'rgba(251,191,36,.16)',
      '--bg-glow-3':           'rgba(251,113,133,.08)',
    },
  },
  {
    id: 'berry-blast',
    nameHe: '🫐 פטל ופיסטוק',
    description: 'ורוד-סגול + מנטה',
    preview: 'linear-gradient(135deg,#ec4899 0%,#a855f7 50%,#34d399 100%)',
    cssVars: {
      '--color-primary':       '#ec4899',
      '--color-primary-light': '#f472b6',
      '--color-primary-dark':  '#db2777',
      '--color-accent':        '#a855f7',
      '--color-accent-light':  '#c084fc',
      '--color-accent-dark':   '#9333ea',
      '--color-hero':          '#34d399',
      '--color-hero-light':    '#6ee7b7',
      '--color-hero-dark':     '#10b981',
      '--bg-glow-1':           'rgba(236,72,153,.18)',
      '--bg-glow-2':           'rgba(168,85,247,.14)',
      '--bg-glow-3':           'rgba(52,211,153,.08)',
    },
  },
  {
    id: 'forest-friends',
    nameHe: '🌲 חברי היער',
    description: 'ירוק חי + תפוז ליים',
    preview: 'linear-gradient(135deg,#10b981 0%,#84cc16 50%,#f97316 100%)',
    cssVars: {
      '--color-primary':       '#10b981',
      '--color-primary-light': '#34d399',
      '--color-primary-dark':  '#059669',
      '--color-accent':        '#84cc16',
      '--color-accent-light':  '#a3e635',
      '--color-accent-dark':   '#65a30d',
      '--color-hero':          '#f97316',
      '--color-hero-light':    '#fb923c',
      '--color-hero-dark':     '#ea580c',
      '--bg-glow-1':           'rgba(16,185,129,.18)',
      '--bg-glow-2':           'rgba(132,204,22,.14)',
      '--bg-glow-3':           'rgba(249,115,22,.08)',
    },
  },
];

const THEME_KEY = 'bagrut_theme';
const DEFAULT_THEME_ID: ThemeId = 'purple-dawn';

export function getThemeId(): ThemeId {
  try {
    const v = localStorage.getItem(THEME_KEY);
    if (v && THEMES.some(t => t.id === v)) return v as ThemeId;
  } catch { /* */ }
  return DEFAULT_THEME_ID;
}

export function setThemeId(id: ThemeId): void {
  try { localStorage.setItem(THEME_KEY, id); } catch { /* */ }
  applyTheme(id);
}

export function getTheme(id?: ThemeId): Theme {
  const target = id ?? getThemeId();
  return THEMES.find(t => t.id === target) ?? THEMES[0];
}

/** Apply a theme by mutating CSS custom properties on :root. */
export function applyTheme(id: ThemeId): void {
  const theme = getTheme(id);
  const root = document.documentElement;
  Object.entries(theme.cssVars).forEach(([k, v]) => root.style.setProperty(k, v));
}
